import * as React from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';

import { cn } from '../lib/utils';

/**
 * 13px rows on hairline dividers, with generous vertical padding. Super Admin's
 * day-to-day is scanning tables, so rows get room and a clear hover state.
 */
const Table = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-x-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-meta', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      'bg-surface-header [&_tr]:border-b-hairline [&_tr]:border-border',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t-hairline border-border bg-background font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b-hairline border-border-subtle transition-colors hover:bg-surface-tint data-[state=selected]:bg-primary-tint',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

/**
 * Column header. Pass `sortable` with `sortDirection` ('asc' | 'desc' | null)
 * and `onSort` to get the indicator and the correct aria-sort announcement.
 */
const TableHead = React.forwardRef(
  ({ className, children, sortable = false, sortDirection = null, onSort, ...props }, ref) => {
    const ariaSort = !sortable
      ? undefined
      : sortDirection === 'asc'
        ? 'ascending'
        : sortDirection === 'desc'
          ? 'descending'
          : 'none';

    const SortIcon =
      sortDirection === 'asc' ? ChevronUp : sortDirection === 'desc' ? ChevronDown : ChevronsUpDown;

    return (
      <th
        ref={ref}
        aria-sort={ariaSort}
        className={cn(
          'h-10 px-4 text-left align-middle text-label font-medium uppercase tracking-wide text-primary-on-tint [&:has([role=checkbox])]:pr-0',
          className,
        )}
        {...props}
      >
        {sortable ? (
          <button
            type="button"
            onClick={onSort}
            className="-mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {children}
            <SortIcon
              className={cn(
                'h-3.5 w-3.5',
                sortDirection ? 'text-primary opacity-100' : 'opacity-50',
              )}
              aria-hidden="true"
            />
          </button>
        ) : (
          children
        )}
      </th>
    );
  },
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('px-4 py-3 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-meta text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
